import { useTheme } from '@mui/material';
import Stack, { StackProps } from '@mui/material/Stack';
import Avatar from '@mui/material/Avatar';
import Divider from '@mui/material/Divider';
import Typography from '@mui/material/Typography';
import { useInView } from 'react-intersection-observer';
import moment from 'moment';

import { useSession } from '@hooks';
import { parseEmojis } from '@utils';
import { GetConversationQuery } from '@graphql';
import { useTranslation } from 'react-i18next';

interface MessageProps {
  message: GetConversationQuery['conversation']['messages']['edges'][0]['node'];
  members: GetConversationQuery['conversation']['members'];
  showMeta?: boolean;
  groupedTop?: boolean;
  groupedBottom?: boolean;
  addSeparator?: boolean;
  addStartOfConversation?: boolean;
  sx?: StackProps['sx'];
}

export function Message(props: Readonly<MessageProps>) {
  const {
    message,
    members,
    showMeta = true,
    groupedTop = false,
    groupedBottom = false,
    addSeparator = false,
    addStartOfConversation = false,
    sx,
  } = props;
  const theme = useTheme();
  const { account } = useSession();
  const { t } = useTranslation('conversation');

  const { ref } = useInView({
    threshold: 0.5,
    triggerOnce: true,
  });

  const parsed = parseEmojis(message.content);

  if (!account) {
    return null;
  }

  const isMine = message.sender.id === account.id;
  const AVATAR = 30;
  const radius = '15px';
  let borderRadius: string | number = radius;

  if (isMine) {
    borderRadius = theme.spacing(radius, groupedTop ? 0.5 : radius, groupedBottom ? 0.5 : radius, radius);
  } else {
    borderRadius = theme.spacing(groupedTop ? 0.5 : radius, radius, radius, groupedBottom ? 0.5 : radius);
  }

  return (
    <>
      {/* DISPLAY NEW UNREAD MESSAGES SEPARATOR */}
      {addSeparator && (
        <Divider textAlign="center" light flexItem sx={{ opacity: 0.6 }}>
          {t('New messages')}
        </Divider>
      )}

      {/* DISPLAY MESSAGE */}
      <Stack
        key={message.id}
        ref={ref}
        direction="row"
        spacing={1}
        alignItems="center"
        justifyContent={message.sender.id === account.id ? 'flex-end' : 'flex-start '}
        mb={showMeta ? 2 : 0}
        sx={sx}
      >
        {!isMine && (
          <Avatar
            src={message.sender.avatar?.url}
            sx={{ width: AVATAR, height: AVATAR }}
            style={{ opacity: showMeta ? 1 : 0 }}
          />
        )}

        <Stack spacing={0.5} maxWidth="70%">
          {members.length > 2 && showMeta && (
            <Typography
              color="text.secondary"
              variant="body2"
              align={message.sender.id === account.id ? 'right' : 'left'}
            >
              {message.sender.name}
            </Typography>
          )}

          <Stack
            p={parsed.isOnlyEmojis ? 0 : 1.5}
            sx={{
              width: 'fit-content',
              borderRadius,
              backgroundColor: parsed.isOnlyEmojis
                ? 'transparent'
                : message.sender.id === account.id
                  ? theme.palette.primary.main
                  : theme.palette.grey[800],
              alignSelf: message.sender.id === account.id ? 'flex-end' : 'flex-start',
              wordBreak: 'break-word',
            }}
          >
            <Typography
              variant="body2"
              align={message.sender.id === account.id ? 'right' : 'left'}
              sx={{ whiteSpace: 'pre-line' }}
              fontSize={parsed.isOnlyEmojis ? (parsed.count <= 8 ? '3.5rem' : '2.5rem') : '.8rem'}
            >
              {message.content}
            </Typography>
          </Stack>

          {showMeta && ( // + show only on last of the group
            <Typography
              variant="body2"
              align={message.sender.id === account.id ? 'right' : 'left'}
              sx={{ whiteSpace: 'pre-line' }}
              fontSize=".6rem"
              color="textSecondary"
            >
              {moment(message.createdAt).fromNow()}
            </Typography>
          )}
        </Stack>
      </Stack>
      {/* DISPLAY NEW UNREAD MESSAGES SEPARATOR */}
      {addStartOfConversation && (
        <Divider textAlign="center" light flexItem sx={{ opacity: 0.6, py: 3 }}>
          {t('Start of conversation')}
        </Divider>
      )}
    </>
  );
}
